import React, { useCallback, useEffect, useState } from 'react';
import { Header } from './components/Header';
import { AudioPlayer } from './components/AudioPlayer';
import { PhraseGrid } from './components/PhraseGrid';
import { GameControls } from './components/GameControls';
import { WinModal } from './components/WinModal';
import { getRandomPhrase } from './data/phrases';
import { audioEngine } from './services/audioEngine';
import { CloudflareConfig, Difficulty, GameStats, PhraseItem, VoiceId } from './types/game';
import { Loader2 } from 'lucide-react';

const STATS_STORAGE_KEY = 'backtalk_game_stats_v3';
const CF_CONFIG_STORAGE_KEY = 'backtalk_cf_config_v1';
const VOICE_STORAGE_KEY = 'backtalk_voice_v1';

const defaultStats: GameStats = {
  solved: 0,
  streak: 0,
  bestStreak: 0,
  hintsUsed: 0,
  replaysCount: 0,
};

export const App: React.FC = () => {
  // Game Configuration State
  const [difficulty, setDifficulty] = useState<Difficulty>('easy');
  const [voice, setVoice] = useState<VoiceId>(() => {
    try {
      const saved = localStorage.getItem(VOICE_STORAGE_KEY);
      return (saved as VoiceId) || 'asteria';
    } catch (_) {
      return 'asteria';
    }
  });

  const [currentPhraseItem, setCurrentPhraseItem] = useState<PhraseItem>(() =>
    getRandomPhrase('easy')
  );

  // Cloudflare Credentials State
  const [cfConfig, setCfConfig] = useState<CloudflareConfig | null>(() => {
    try {
      const saved = localStorage.getItem(CF_CONFIG_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (_) {
      return null;
    }
  });

  const [isCloudflareActive, setIsCloudflareActive] = useState<boolean>(false);

  // Direct In-Blank Gameplay State
  const [userInputs, setUserInputs] = useState<string[]>([]);
  const [revealedPositions, setRevealedPositions] = useState<Set<number>>(new Set());
  const [hintsUsed, setHintsUsed] = useState<number>(0);
  const [replaysCount, setReplaysCount] = useState<number>(0);
  const [isWon, setIsWon] = useState<boolean>(false);
  const [isRevealed, setIsRevealed] = useState<boolean>(false);

  // Audio Engine State
  const [isAudioLoading, setIsAudioLoading] = useState<boolean>(false);
  const [isAudioReady, setIsAudioReady] = useState<boolean>(false);
  const [isPlayingReverse, setIsPlayingReverse] = useState<boolean>(false);
  const [isPlayingForward, setIsPlayingForward] = useState<boolean>(false);

  // Persistent Stats State
  const [stats, setStats] = useState<GameStats>(() => {
    try {
      const saved = localStorage.getItem(STATS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultStats;
    } catch (_) {
      return defaultStats;
    }
  });

  // Save Voice preference
  useEffect(() => {
    try {
      localStorage.setItem(VOICE_STORAGE_KEY, voice);
    } catch (_) {}
  }, [voice]);

  // Sync Cloudflare Config with AudioEngine
  useEffect(() => {
    audioEngine.setCloudflareConfig(cfConfig);
    try {
      if (cfConfig) {
        localStorage.setItem(CF_CONFIG_STORAGE_KEY, JSON.stringify(cfConfig));
      } else {
        localStorage.removeItem(CF_CONFIG_STORAGE_KEY);
      }
    } catch (_) {}
  }, [cfConfig]);

  // Save stats to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(stats));
    } catch (_) {}
  }, [stats]);

  // Initialize userInputs array whenever currentPhraseItem changes
  useEffect(() => {
    const totalLetters = currentPhraseItem.phrase.replace(/[^a-zA-Z]/g, '').length;
    setUserInputs(new Array(totalLetters).fill(''));
  }, [currentPhraseItem]);

  // Load TTS Audio for current phrase and selected voice
  const loadPhraseAudio = useCallback(async (phrase: PhraseItem, targetVoice?: VoiceId) => {
    const voiceToUse = targetVoice || voice;
    setIsAudioLoading(true);
    setIsAudioReady(false);
    audioEngine.stop();

    try {
      await audioEngine.loadPhraseAudio(phrase.phrase, voiceToUse);
      setIsAudioReady(true);
      setIsCloudflareActive(audioEngine.isCloudflareActive());
    } catch (err) {
      console.error('Failed to load audio:', err);
    } finally {
      setIsAudioLoading(false);
    }
  }, [voice]);

  // Initialize new round
  const startNewRound = useCallback(
    (newDiff?: Difficulty) => {
      const diffToUse = newDiff || difficulty;
      const nextPhrase = getRandomPhrase(diffToUse, currentPhraseItem.id);

      setCurrentPhraseItem(nextPhrase);
      const totalLetters = nextPhrase.phrase.replace(/[^a-zA-Z]/g, '').length;
      setUserInputs(new Array(totalLetters).fill(''));
      setRevealedPositions(new Set());
      setHintsUsed(0);
      setReplaysCount(0);
      setIsWon(false);
      setIsRevealed(false);
      setIsPlayingReverse(false);
      setIsPlayingForward(false);

      loadPhraseAudio(nextPhrase);
    },
    [difficulty, currentPhraseItem.id, loadPhraseAudio]
  );

  // Load initial round on mount
  useEffect(() => {
    loadPhraseAudio(currentPhraseItem, voice);
  }, []);

  // Change voice selection
  const handleSelectVoice = (newVoice: VoiceId) => {
    setVoice(newVoice);
    loadPhraseAudio(currentPhraseItem, newVoice);
  };

  // Change difficulty
  const handleSelectDifficulty = (newDiff: Difficulty) => {
    setDifficulty(newDiff);
    startNewRound(newDiff);
  };

  // Play Reverse Audio
  const handlePlayReverse = useCallback(() => {
    if (!isAudioReady || isPlayingReverse) return;
    setIsPlayingReverse(true);
    setReplaysCount((prev) => prev + 1);
    setStats((prev) => ({ ...prev, replaysCount: prev.replaysCount + 1 }));

    audioEngine.playReverse(() => {
      setIsPlayingReverse(false);
    });
  }, [isAudioReady, isPlayingReverse]);

  // Play Forward Audio
  const handlePlayForward = useCallback(() => {
    if (!isAudioReady || isPlayingForward) return;
    setIsPlayingForward(true);

    audioEngine.playForward(() => {
      setIsPlayingForward(false);
    });
  }, [isAudioReady, isPlayingForward]);

  // Check victory condition
  const checkWinCondition = useCallback(
    (inputs: string[], hints: Set<number>) => {
      if (isWon || isRevealed) return;

      const upperPhrase = currentPhraseItem.phrase.toUpperCase();
      let slotIdx = 0;
      let isCompleteAndMatch = true;

      for (let i = 0; i < upperPhrase.length; i++) {
        const char = upperPhrase[i];
        if (/[A-Z]/.test(char)) {
          const isHinted = hints.has(i);
          const typed = inputs[slotIdx] || '';
          if (!isHinted && typed !== char) {
            isCompleteAndMatch = false;
            break;
          }
          slotIdx++;
        }
      }

      if (isCompleteAndMatch) {
        setIsWon(true);
        setStats((prev) => {
          const newStreak = prev.streak + 1;
          return {
            ...prev,
            solved: prev.solved + 1,
            streak: newStreak,
            bestStreak: Math.max(prev.bestStreak, newStreak),
          };
        });

        setTimeout(() => {
          handlePlayForward();
        }, 300);
      }
    },
    [currentPhraseItem.phrase, isWon, isRevealed, handlePlayForward]
  );

  // Handle direct in-blank character input
  const handleInputChange = (slotIdx: number, char: string) => {
    if (isWon || isRevealed) return;

    const nextInputs = [...userInputs];
    nextInputs[slotIdx] = char;
    setUserInputs(nextInputs);

    checkWinCondition(nextInputs, revealedPositions);
  };

  // Handle Backspace inside blanks
  const handleBackspace = (slotIdx: number) => {
    if (isWon || isRevealed) return;

    const nextInputs = [...userInputs];
    if (nextInputs[slotIdx]) {
      nextInputs[slotIdx] = '';
    } else if (slotIdx > 0) {
      nextInputs[slotIdx - 1] = '';
    }
    setUserInputs(nextInputs);
  };

  const handleNavigate = (_slotIdx: number, _direction: 'left' | 'right') => {};

  // Handle Ask for Hint
  const handleAskHint = useCallback(() => {
    if (isWon || isRevealed) return;

    const phrase = currentPhraseItem.phrase.toUpperCase();
    const unrevealedIndices: number[] = [];

    for (let i = 0; i < phrase.length; i++) {
      if (/[A-Z]/.test(phrase[i]) && !revealedPositions.has(i)) {
        unrevealedIndices.push(i);
      }
    }

    if (unrevealedIndices.length > 0) {
      const randomIndex = unrevealedIndices[Math.floor(Math.random() * unrevealedIndices.length)];
      const nextRevealed = new Set(revealedPositions);
      nextRevealed.add(randomIndex);

      setRevealedPositions(nextRevealed);
      setHintsUsed((prev) => prev + 1);
      setStats((prev) => ({ ...prev, hintsUsed: prev.hintsUsed + 1 }));

      checkWinCondition(userInputs, nextRevealed);
    }
  }, [isWon, isRevealed, currentPhraseItem.phrase, revealedPositions, userInputs, checkWinCondition]);

  // Give Up / Reveal Phrase
  const handleReveal = useCallback(() => {
    if (isWon || isRevealed) return;
    setIsRevealed(true);
    setStats((prev) => ({ ...prev, streak: 0 }));
    handlePlayForward();
  }, [isWon, isRevealed, handlePlayForward]);

  // Reset Stats
  const handleResetStats = useCallback(() => {
    setStats(defaultStats);
    try {
      localStorage.removeItem(STATS_STORAGE_KEY);
    } catch (_) {}
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-between p-3 sm:p-6 max-w-5xl mx-auto selection:bg-indigo-500 selection:text-white">
      {/* Header Bar */}
      <Header
        difficulty={difficulty}
        voice={voice}
        stats={stats}
        cfConfig={cfConfig}
        isCloudflareActive={isCloudflareActive}
        onSelectDifficulty={handleSelectDifficulty}
        onSelectVoice={handleSelectVoice}
        onSaveCfConfig={setCfConfig}
        onResetStats={handleResetStats}
      />

      {/* Main Game Area */}
      <main className="w-full flex-1 flex flex-col items-center justify-center gap-4 max-w-3xl my-2">
        {/* Audio Player & Wave Visualizer */}
        <AudioPlayer
          isPlaying={isPlayingReverse}
          isPlayingForward={isPlayingForward}
          isAudioReady={isAudioReady}
          isWon={isWon || isRevealed}
          replaysCount={replaysCount}
          onPlayReverse={handlePlayReverse}
          onPlayForward={handlePlayForward}
        />

        {/* Audio Loading Indicator */}
        {isAudioLoading && (
          <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium animate-pulse py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Generating speech with Cloudflare AI voice ({voice})...</span>
          </div>
        )}

        {/* Interactive Direct In-Blank Input Grid */}
        <PhraseGrid
          phrase={currentPhraseItem.phrase}
          revealedPositions={revealedPositions}
          userInputs={userInputs}
          isWon={isWon}
          isRevealed={isRevealed}
          onInputChange={handleInputChange}
          onBackspace={handleBackspace}
          onNavigate={handleNavigate}
          onSubmit={() => checkWinCondition(userInputs, revealedPositions)}
        />

        {/* Win Celebration Modal */}
        {isWon && (
          <WinModal
            phraseItem={currentPhraseItem}
            replaysCount={replaysCount}
            hintsUsed={hintsUsed}
            streak={stats.streak}
            onPlayForward={handlePlayForward}
            onNextPhrase={() => startNewRound()}
          />
        )}

        {/* Game Action Controls */}
        <GameControls
          hintsUsed={hintsUsed}
          isWon={isWon}
          isRevealed={isRevealed}
          onAskHint={handleAskHint}
          onReveal={handleReveal}
          onNextPhrase={() => startNewRound()}
        />
      </main>

      {/* Footer */}
      <footer className="w-full text-center text-xs text-slate-500 py-3 mt-4 border-t border-slate-900 flex flex-wrap items-center justify-center gap-3">
        <span className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isCloudflareActive ? 'bg-emerald-400 animate-ping' : 'bg-indigo-400'}`} />
          <strong>Cloudflare Workers AI</strong> (@cf/deepgram/aura-{voice})
        </span>
        <span>•</span>
        <span>Web Audio API Engine</span>
        <span>•</span>
        <span className="text-slate-400">Backtalk © 2026</span>
      </footer>
    </div>
  );
};
